import { Box, Dialog, DialogActions, DialogContent, Typography } from "@mui/material"
import { EditOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"

import { Button } from "@/components/common/Button"
import CancelDialog from "@/components/common/Modals/CancelDialog"
import { FormSelect } from "@/components/UiKit/FormControls/FormSelect"
import type { SalesOrder } from "@/graphql/codegen/graphql"
import { useSalesOrderStatusForm } from "@/screens/CustomerOrders/hooks/useSalesOrderStatusForm"
import { toSalesOrderStatusOptions } from "@/screens/CustomerOrders/utils"

interface SalesOrderStatusFormDialogProps {
  salesOrder: SalesOrder
  onClose: () => void
  onSubmit: () => void
}

export const SalesOrderStatusFormDialog: FC<SalesOrderStatusFormDialogProps> = ({ salesOrder, onClose, onSubmit }) => {
  const {
    methods,
    handleSubmit,
    handleCancel,
    canSubmit,
    isCancelConfirmationOpen,
    setIsCancelConfirmationOpen,
    isSaving,
  } = useSalesOrderStatusForm({ salesOrder, onClose, onSubmit })

  return (
    <Dialog open onClose={handleCancel} maxWidth='sm' fullWidth data-testid='customer-orders.status-form-dialog'>
      {isCancelConfirmationOpen ? (
        <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={onClose} />
      ) : (
        <>
          <DialogContent classes={{ root: "px-10 pt-10 pb-6" }}>
            <Box className='mb-6 flex flex-col items-center gap-6 text-center'>
              <EditOutlined className='size-[48px] text-primary' />
              <Typography
                color='primary'
                component='h2'
                className='text-xl font-normal leading-[133%] tracking-[-1px]'
                data-testid='customer-orders.status-form-dialog.title'
              >
                Update sales order status
              </Typography>
              <Typography className='text-base leading-[150%] text-gray-700'>
                Note: Some statuses are updated automatically based on sales order activity.
              </Typography>
            </Box>
            <FormSelect
              control={methods.control}
              name='status'
              label='Status'
              options={toSalesOrderStatusOptions(salesOrder.status)}
              data-testid='customer-orders.status-form-dialog.status-field'
            />
          </DialogContent>
          <DialogActions className='justify-between px-10 pb-10'>
            <Button
              appearance='outlined'
              disabled={isSaving}
              onClick={handleCancel}
              data-testid='customer-orders.status-form-dialog.cancel-button'
            >
              Cancel
            </Button>
            <Button
              disabled={!canSubmit}
              loading={isSaving}
              onClick={handleSubmit}
              data-testid='customer-orders.status-form-dialog.submit-button'
            >
              Update Status
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}
